import React, { useEffect, useMemo, useState } from 'react';
import {
  ArchiveBoxIcon,
  CalendarIcon,
  PlusIcon,
  TagIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  InventoryLot,
  InventoryItem,
  Product,
  Supplier,
  Tray,
  inventoryLotsAPI,
  productsAPI,
  suppliersAPI,
} from '../../services/api';
import { Badge, Button, Card, CardBody, EmptyState, SearchInput, Spinner } from '../ui';
import InventoryLocationPicker from './InventoryLocationPicker';

type LotItemFormRow = {
  id: string;
  productId: string;
  trayId: string;
  quantity: string;
  unit: string;
  status: InventoryItem['status'];
  expiryDate: string;
  notes: string;
};

const newItemRow = (): LotItemFormRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  productId: '',
  trayId: '',
  quantity: '',
  unit: '',
  status: 'IN_STOCK',
  expiryDate: '',
  notes: '',
});

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
};

const formatLocation = (tray?: Tray) => {
  if (!tray) return 'No location';
  return [
    tray.bin?.shelf?.warehouse?.name,
    tray.bin?.shelf?.code,
    tray.bin?.code,
    tray.code,
  ].filter(Boolean).join(' - ') || 'No location';
};

const InventoryLots: React.FC = () => {
  const [lots, setLots] = useState<InventoryLot[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedLotId, setSelectedLotId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingLot, setSavingLot] = useState(false);
  const [savingItems, setSavingItems] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [search, setSearch] = useState('');
  const [lotForm, setLotForm] = useState({
    lotNumber: '',
    supplierId: '',
    receivedDate: '',
    expiryDate: '',
    notes: '',
  });
  const [itemRows, setItemRows] = useState<LotItemFormRow[]>([newItemRow()]);

  const selectedLot = useMemo(
    () => lots.find((lot) => lot.id === selectedLotId) || lots[0] || null,
    [lots, selectedLotId],
  );

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!selectedLotId && lots.length > 0) {
      setSelectedLotId(lots[0].id);
    }
  }, [lots, selectedLotId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setMessage(null);
      const [lotData, productData, supplierData] = await Promise.all([
        inventoryLotsAPI.getAll(),
        productsAPI.getAll(),
        suppliersAPI.getAll({ limit: 200 }),
      ]);
      setLots(lotData);
      setProducts(productData);
      setSuppliers(supplierData.data || supplierData);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to load lots' });
    } finally {
      setLoading(false);
    }
  };

  const filteredLots = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) return lots;
    return lots.filter((lot) =>
      lot.lotNumber.toLowerCase().includes(normalized) ||
      lot.supplier?.name?.toLowerCase().includes(normalized) ||
      lot.products?.some((product) =>
        product.name.toLowerCase().includes(normalized) ||
        product.sku.toLowerCase().includes(normalized),
      ),
    );
  }, [lots, search]);

  const handleCreateLot = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!lotForm.lotNumber.trim()) return;

    try {
      setSavingLot(true);
      setMessage(null);
      const created = await inventoryLotsAPI.create({
        lotNumber: lotForm.lotNumber.trim(),
        supplierId: lotForm.supplierId ? Number(lotForm.supplierId) : undefined,
        receivedDate: lotForm.receivedDate || undefined,
        expiryDate: lotForm.expiryDate || undefined,
        notes: lotForm.notes.trim() || undefined,
      });
      setLotForm({ lotNumber: '', supplierId: '', receivedDate: '', expiryDate: '', notes: '' });
      await loadData();
      setSelectedLotId(created.id);
      setMessage({ type: 'success', text: `Lot ${created.lotNumber} created` });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to create lot' });
    } finally {
      setSavingLot(false);
    }
  };

  const updateItemRow = (id: string, patch: Partial<LotItemFormRow>) => {
    setItemRows((current) => current.map((row) => row.id === id ? { ...row, ...patch } : row));
  };

  const getProductUnit = (productId: string) =>
    products.find((product) => product.id === Number(productId))?.unit || '';

  const handleAddItems = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedLot) return;

    const payload = itemRows
      .filter((row) => row.productId && row.trayId && Number(row.quantity) > 0)
      .map((row) => ({
        productId: Number(row.productId),
        trayId: Number(row.trayId),
        quantity: Number(row.quantity),
        unit: row.unit.trim() || getProductUnit(row.productId) || undefined,
        status: row.status,
        expiryDate: row.expiryDate ? new Date(row.expiryDate).toISOString() : undefined,
        notes: row.notes.trim() || undefined,
      }));

    if (payload.length === 0) {
      setMessage({ type: 'error', text: 'Add at least one product row with product, location, and quantity' });
      return;
    }

    try {
      setSavingItems(true);
      setMessage(null);
      await inventoryLotsAPI.addItems(selectedLot.id, payload);
      setItemRows([newItemRow()]);
      await loadData();
      setSelectedLotId(selectedLot.id);
      setMessage({ type: 'success', text: `Added ${payload.length} item row${payload.length === 1 ? '' : 's'} to ${selectedLot.lotNumber}` });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to add products to lot' });
    } finally {
      setSavingItems(false);
    }
  };

  const handleDeactivateLot = async (lot: InventoryLot) => {
    if (!window.confirm(`Deactivate lot ${lot.lotNumber}?`)) return;
    try {
      await inventoryLotsAPI.delete(lot.id);
      await loadData();
      setMessage({ type: 'success', text: `Lot ${lot.lotNumber} deactivated` });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to deactivate lot' });
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">Lots</h2>
          <p className="mt-1 text-sm text-neutral-600">Create inventory lots and add product quantities into warehouse locations.</p>
        </div>
        <div className="w-full lg:w-80">
          <SearchInput value={search} onChange={setSearch} placeholder="Search lots, suppliers, products..." />
        </div>
      </div>

      {message && (
        <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${
          message.type === 'success'
            ? 'border-success-200 bg-success-50 text-success-700'
            : 'border-error-200 bg-error-50 text-error-700'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card variant="elevated" className="xl:col-span-1">
          <CardBody className="p-5">
            <div className="mb-4 flex items-center gap-2">
              <TagIcon className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-neutral-900">Create Lot</h3>
            </div>
            <form onSubmit={handleCreateLot} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-neutral-700">Lot Number *</label>
                <input
                  value={lotForm.lotNumber}
                  onChange={(event) => setLotForm({ ...lotForm, lotNumber: event.target.value })}
                  required
                  className="mt-1 block w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700">Supplier</label>
                <select
                  value={lotForm.supplierId}
                  onChange={(event) => setLotForm({ ...lotForm, supplierId: event.target.value })}
                  className="mt-1 block w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">No supplier</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700">Received</label>
                  <input
                    type="date"
                    value={lotForm.receivedDate}
                    onChange={(event) => setLotForm({ ...lotForm, receivedDate: event.target.value })}
                    className="mt-1 block w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700">Expiry</label>
                  <input
                    type="date"
                    value={lotForm.expiryDate}
                    onChange={(event) => setLotForm({ ...lotForm, expiryDate: event.target.value })}
                    className="mt-1 block w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700">Notes</label>
                <textarea
                  value={lotForm.notes}
                  onChange={(event) => setLotForm({ ...lotForm, notes: event.target.value })}
                  rows={3}
                  className="mt-1 block w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <Button type="submit" variant="primary" isLoading={savingLot} leftIcon={<PlusIcon className="h-4 w-4" />}>
                Create Lot
              </Button>
            </form>
          </CardBody>
        </Card>

        <div className="space-y-3 xl:col-span-2 md:hidden">
          {filteredLots.length === 0 ? (
            <Card variant="elevated">
              <CardBody className="p-4">
                <EmptyState
                  icon={<ArchiveBoxIcon className="h-12 w-12" />}
                  title="No lots found"
                  description="Create a lot or adjust your search."
                />
              </CardBody>
            </Card>
          ) : filteredLots.map((lot) => (
            <Card key={lot.id} variant="elevated">
              <CardBody className={`p-4 ${selectedLot?.id === lot.id ? 'bg-primary-50' : ''}`}>
                <button type="button" onClick={() => setSelectedLotId(lot.id)} className="block w-full text-left">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-neutral-900">{lot.lotNumber}</p>
                      <p className="mt-1 text-xs text-neutral-500">{lot.supplier?.name || 'No supplier'}</p>
                    </div>
                    <Badge variant={lot.isActive ? 'success' : 'neutral'} size="sm">
                      {lot.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-xs font-medium uppercase text-neutral-500">Products</p>
                      <p className="mt-1 font-semibold text-neutral-900">{lot.productCount || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase text-neutral-500">Available</p>
                      <p className="mt-1 font-semibold text-neutral-900">{lot.availableQuantity || 0} / {lot.totalQuantity || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase text-neutral-500">Expiry</p>
                      <p className="mt-1 text-neutral-700">{formatDate(lot.expiryDate)}</p>
                    </div>
                  </div>
                </button>
              </CardBody>
            </Card>
          ))}
        </div>

        <Card variant="elevated" className="hidden md:block xl:col-span-2">
          <CardBody className="p-0">
            {filteredLots.length === 0 ? (
              <EmptyState
                icon={<ArchiveBoxIcon className="h-12 w-12" />}
                title="No lots found"
                description="Create a lot or adjust your search."
              />
            ) : (
              <div className="max-h-[520px] overflow-auto">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="sticky top-0 bg-neutral-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-medium uppercase text-neutral-500">Lot</th>
                      <th className="px-5 py-3 text-left text-xs font-medium uppercase text-neutral-500">Supplier</th>
                      <th className="px-5 py-3 text-left text-xs font-medium uppercase text-neutral-500">Products</th>
                      <th className="px-5 py-3 text-left text-xs font-medium uppercase text-neutral-500">Available</th>
                      <th className="px-5 py-3 text-left text-xs font-medium uppercase text-neutral-500">Expiry</th>
                      <th className="px-5 py-3 text-right text-xs font-medium uppercase text-neutral-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200 bg-white">
                    {filteredLots.map((lot) => (
                      <tr
                        key={lot.id}
                        className={`cursor-pointer transition-colors ${selectedLot?.id === lot.id ? 'bg-primary-50' : 'hover:bg-neutral-50'}`}
                        onClick={() => setSelectedLotId(lot.id)}
                      >
                        <td className="px-5 py-4">
                          <div className="font-medium text-neutral-900">{lot.lotNumber}</div>
                          <div className="mt-1">
                            <Badge variant={lot.isActive ? 'success' : 'neutral'} size="sm">
                              {lot.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-neutral-600">{lot.supplier?.name || '-'}</td>
                        <td className="px-5 py-4 text-sm text-neutral-600">{lot.productCount || 0}</td>
                        <td className="px-5 py-4 text-sm text-neutral-900">{lot.availableQuantity || 0} / {lot.totalQuantity || 0}</td>
                        <td className="px-5 py-4 text-sm text-neutral-600">{formatDate(lot.expiryDate)}</td>
                        <td className="px-5 py-4 text-right">
                          {lot.isActive && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDeactivateLot(lot);
                              }}
                              className="inline-flex rounded-md p-1.5 text-error-600 transition-colors hover:bg-error-50 hover:text-error-800"
                              title="Deactivate lot"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {selectedLot && (
        <Card variant="elevated">
          <CardBody className="p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-neutral-900">{selectedLot.lotNumber}</h3>
                  <Badge variant={selectedLot.isActive ? 'success' : 'neutral'}>{selectedLot.isActive ? 'Active' : 'Inactive'}</Badge>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-neutral-500">
                  <span>{selectedLot.supplier?.name || 'No supplier'}</span>
                  <span>{selectedLot.productCount || 0} products</span>
                  <span>{selectedLot.availableQuantity || 0} available</span>
                  <span className="inline-flex items-center gap-1">
                    <CalendarIcon className="h-4 w-4" />
                    {formatDate(selectedLot.expiryDate)}
                  </span>
                </div>
                {selectedLot.notes && <p className="mt-3 text-sm text-neutral-600">{selectedLot.notes}</p>}
              </div>
            </div>

            <form onSubmit={handleAddItems} className="mt-6 space-y-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-neutral-900">Add Products To Lot</h4>
                  <p className="mt-1 text-xs text-neutral-500">Each row creates an inventory item linked to this lot.</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setItemRows((current) => [...current, newItemRow()])}
                  leftIcon={<PlusIcon className="h-4 w-4" />}
                >
                  Add Row
                </Button>
              </div>

              <div className="space-y-3">
                {itemRows.map((row) => (
                  <div key={row.id} className="grid grid-cols-1 gap-3 rounded-lg border border-neutral-200 bg-white p-3 lg:grid-cols-12">
                    <div className="lg:col-span-2">
                      <label className="block text-xs font-medium text-neutral-600">Product *</label>
                      <select
                        value={row.productId}
                        onChange={(event) => {
                          const nextProductId = event.target.value;
                          updateItemRow(row.id, {
                            productId: nextProductId,
                            unit: getProductUnit(nextProductId) || row.unit,
                          });
                        }}
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      >
                        <option value="">Select product</option>
                        {products.map((product) => (
                          <option key={product.id} value={product.id}>{product.name} ({product.sku})</option>
                        ))}
                      </select>
                    </div>
                    <div className="lg:col-span-4">
                      <InventoryLocationPicker
                        trayId={row.trayId}
                        onTrayIdChange={(trayId) => updateItemRow(row.id, { trayId })}
                        required
                      />
                    </div>
                    <div className="lg:col-span-1">
                      <label className="block text-xs font-medium text-neutral-600">Qty *</label>
                      <input
                        type="number"
                        min="1"
                        value={row.quantity}
                        onChange={(event) => updateItemRow(row.id, { quantity: event.target.value })}
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div className="lg:col-span-1">
                      <label className="block text-xs font-medium text-neutral-600">Unit</label>
                      <input
                        type="text"
                        value={row.unit}
                        onChange={(event) => updateItemRow(row.id, { unit: event.target.value })}
                        placeholder={getProductUnit(row.productId) || 'unit'}
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div className="lg:col-span-1">
                      <label className="block text-xs font-medium text-neutral-600">Status</label>
                      <select
                        value={row.status}
                        onChange={(event) => updateItemRow(row.id, { status: event.target.value as InventoryItem['status'] })}
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      >
                        <option value="IN_STOCK">In Stock</option>
                        <option value="LOW_STOCK">Low Stock</option>
                        <option value="RESERVED">Reserved</option>
                        <option value="DAMAGED">Damaged</option>
                        <option value="OUT_OF_STOCK">Out of Stock</option>
                      </select>
                    </div>
                    <div className="lg:col-span-2">
                      <label className="block text-xs font-medium text-neutral-600">Expiry</label>
                      <input
                        type="date"
                        value={row.expiryDate}
                        onChange={(event) => updateItemRow(row.id, { expiryDate: event.target.value })}
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div className="flex items-end justify-end lg:col-span-1">
                      <button
                        type="button"
                        onClick={() => setItemRows((current) => current.length === 1 ? [newItemRow()] : current.filter((item) => item.id !== row.id))}
                        className="rounded-md p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-error-700"
                        title="Remove row"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="lg:col-span-12">
                      <input
                        type="text"
                        value={row.notes}
                        onChange={(event) => updateItemRow(row.id, { notes: event.target.value })}
                        placeholder="Optional row notes"
                        className="block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end">
                <Button type="submit" variant="primary" isLoading={savingItems}>
                  Add To Lot
                </Button>
              </div>
            </form>

            <div className="mt-6 space-y-3 md:hidden">
              {(selectedLot.inventoryItems || []).length === 0 ? (
                <p className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500">
                  No product quantities have been added to this lot yet.
                </p>
              ) : selectedLot.inventoryItems?.map((item) => (
                <div key={item.id} className="rounded-lg border border-neutral-200 bg-white p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-neutral-900">
                        {item.product?.name || 'Unknown'}
                      </p>
                      <p className="mt-1 text-xs text-neutral-500">{item.product?.sku || '-'}</p>
                    </div>
                    <Badge variant="neutral" size="sm">{item.status.replace(/_/g, ' ')}</Badge>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs font-medium uppercase text-neutral-500">Quantity</p>
                      <p className="mt-1 font-semibold text-neutral-900">{item.quantity} {item.unit || item.product?.unit || ''}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase text-neutral-500">Expiry</p>
                      <p className="mt-1 text-neutral-700">{formatDate(item.expiryDate)}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs font-medium uppercase text-neutral-500">Location</p>
                      <p className="mt-1 text-neutral-700">{item.location || formatLocation(item.tray)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 hidden overflow-x-auto rounded-lg border border-neutral-200 md:block">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-neutral-500">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-neutral-500">Quantity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-neutral-500">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-neutral-500">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-neutral-500">Expiry</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 bg-white">
                  {(selectedLot.inventoryItems || []).length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-neutral-500">
                        No product quantities have been added to this lot yet.
                      </td>
                    </tr>
                  ) : (
                    selectedLot.inventoryItems?.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3 text-sm font-medium text-neutral-900">
                          {item.product?.name || 'Unknown'} <span className="text-neutral-400">{item.product?.sku || ''}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-700">{item.quantity} {item.unit || item.product?.unit || ''}</td>
                        <td className="px-4 py-3 text-sm text-neutral-700">{item.status.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3 text-sm text-neutral-700">{item.location || formatLocation(item.tray)}</td>
                        <td className="px-4 py-3 text-sm text-neutral-700">{formatDate(item.expiryDate)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default InventoryLots;
